"use client";

import React, { useEffect, useState } from "react";

import BlogCard from "./BlogCard";

// Custom hook for responsive styles
function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: 1024, // Default to desktop size to prevent flicker on initial render
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
      });
    }

    if (typeof window !== "undefined") {
      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  return windowSize;
}

interface BlogsListProps {
  blogs: any[]; // Accept plain objects from server
}

// OPTION 1: CSS Grid with auto-fit and 384px minimum (Recommended)
export function BlogContent({ blogs }: BlogsListProps) {
  return (
    <div className="w-full">
      <div className="container mx-auto px-4">
        {/* Auto-fit grid with 384px minimum width */}
        <div
          className="grid gap-6"
          style={{
            gridTemplateColumns:
              "repeat(auto-fit, minmax(clamp(200px, 30%, 384px), 1fr))",
          }}
        >
          {blogs.map((blog) => (
            <BlogCard key={blog.slug} blog={blog} />
          ))}
        </div>
      </div>
    </div>
  );
}

// OPTION 2: Dynamic calculation based on screen width
export function BlogContentDynamic({ blogs }: BlogsListProps) {
  const { width } = useWindowSize();

  const getGridConfig = () => {
    if (typeof width !== "number" || width === 0) {
      return {
        gridTemplateColumns: "repeat(auto-fit, minmax(384px, 1fr))",
        gap: "24px",
      };
    }

    const containerPadding = 32; // 16px on each side
    const gap = 24;
    const availableWidth = width - containerPadding;

    // Calculate how many 384px cards can fit
    let cardsPerRow = 1;
    let testWidth = 384;

    while (testWidth + gap + 384 <= availableWidth) {
      cardsPerRow++;
      testWidth += gap + 384;
    }

    // Create grid template based on calculated cards per row
    return {
      gridTemplateColumns: `repeat(${cardsPerRow}, minmax(384px, 1fr))`,
      gap: "24px",
    };
  };

  const gridConfig = getGridConfig();

  return (
    <div className="w-full">
      <div className="container mx-auto px-4">
        <div
          className="grid"
          style={{
            gridTemplateColumns: gridConfig.gridTemplateColumns,
            gap: gridConfig.gap,
          }}
        >
          {blogs.map((blog) => (
            <BlogCard key={blog.slug} blog={blog} />
          ))}
        </div>
      </div>
    </div>
  );
}

// OPTION 3: Responsive with specific breakpoints for 384px cards
export function BlogContentResponsive({ blogs }: BlogsListProps) {
  const { width } = useWindowSize();

  const getGridColumns = () => {
    if (typeof width !== "number" || width === 0) {
      return "repeat(auto-fit, minmax(384px, 1fr))";
    }

    // Calculate exact fit
    const containerWidth = width - 32; // Account for padding
    const cardMinWidth = 384;
    const gap = 24;

    if (containerWidth < cardMinWidth) {
      // Very small screens - single column
      return "1fr";
    } else if (containerWidth < cardMinWidth * 2 + gap) {
      // 1 card fits
      return "minmax(384px, 1fr)";
    } else if (containerWidth < cardMinWidth * 3 + gap * 2) {
      // 2 cards fit
      return "repeat(2, minmax(384px, 1fr))";
    } else if (containerWidth < cardMinWidth * 4 + gap * 3) {
      // 3 cards fit
      return "repeat(3, minmax(384px, 1fr))";
    } else if (containerWidth < cardMinWidth * 5 + gap * 4) {
      // 4 cards fit
      return "repeat(4, minmax(384px, 1fr))";
    } else {
      // 5+ cards fit (for very large screens)
      return "repeat(auto-fit, minmax(384px, 1fr))";
    }
  };

  return (
    <div className="w-full">
      <div className="container mx-auto px-4">
        <div
          className="grid gap-6"
          style={{
            gridTemplateColumns: getGridColumns(),
          }}
        >
          {blogs.map((blog) => (
            <BlogCard key={blog.slug} blog={blog} />
          ))}
        </div>
      </div>
    </div>
  );
}

// OPTION 4: Tailwind CSS classes with custom breakpoints
export function BlogContentTailwind({ blogs }: BlogsListProps) {
  return (
    <div className="w-full">
      <div className="container mx-auto px-4">
        {/* Custom responsive grid using Tailwind classes */}
        <div className="grid gap-6 grid-cols-1 min-[450px]:grid-cols-1 min-[834px]:grid-cols-2 min-[1218px]:grid-cols-3 min-[1602px]:grid-cols-4">
          {blogs.map((blog) => (
            <div className="min-w-[384px]" key={blog.slug}>
              <BlogCard blog={blog} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// OPTION 5: CSS-only solution (add to your CSS file)
export function BlogContentCSS({ blogs }: BlogsListProps) {
  return (
    <div className="w-full">
      <div className="container mx-auto px-4">
        <div className="blog-grid-384">
          {blogs.map((blog) => (
            <BlogCard key={blog.slug} blog={blog} />
          ))}
        </div>
      </div>
    </div>
  );
}

// OPTION 6: With debug info to see how many cards fit
export function BlogContentDebug({ blogs }: BlogsListProps) {
  const { width } = useWindowSize();

  const calculateCardsPerRow = () => {
    if (!width) return 1;

    const containerPadding = 32;
    const gap = 24;
    const cardMinWidth = 384;
    const availableWidth = width - containerPadding;

    let cardsPerRow = Math.floor(availableWidth / cardMinWidth);

    // Check if we can fit the gaps
    const totalGapWidth = (cardsPerRow - 1) * gap;
    const totalCardWidth = cardsPerRow * cardMinWidth;

    while (totalCardWidth + totalGapWidth > availableWidth && cardsPerRow > 1) {
      cardsPerRow--;
    }

    return Math.max(1, cardsPerRow);
  };

  const cardsPerRow = calculateCardsPerRow();

  return (
    <div className="w-full ">
      <div className="container mx-auto px-4">
        {/* Debug info */}
        <div className="mb-4 p-3 bg-gray-100 rounded text-sm">
          <p>Screen width: {width}px</p>
          <p>Cards per row: {cardsPerRow}</p>
          <p>Available width: {width - 32}px</p>
        </div>

        <div
          className="grid gap-6"
          style={{
            gridTemplateColumns: `repeat(${cardsPerRow}, minmax(384px, 1fr))`,
          }}
        >
          {blogs.map((blog) => (
            <BlogCard key={blog.slug} blog={blog} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* 
CSS for Option 5 - Add this to your stylesheet:

.blog-grid-384 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(384px, 1fr));
  gap: 1.5rem;
}

@media (max-width: 450px) {
  .blog-grid-384 {
    grid-template-columns: 1fr;
  }
}
*/
